import { getMoneyValue } from "@tosize/shapes/src/lib/utils";
import type { Finish } from "@tosize/shapes/src/types/finish";
import type { FinishItemPostData } from "@tosize/shapes/src/types/workpiece";
import { formatNumber, useNotifications } from "@tosize/ui";
import type { Checkbox } from "radix-ui";
import { type FC, Fragment, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { usePostDataContext } from "../../../../../context/PostDataContext";
import useFinishSide from "../../../../../hooks/useFinishSide";
import useSelectedFinishes from "../../../../../hooks/useSelectedFinishes";
import { FinishOptionCheckbox } from "../../../FinishOptionCheckbox";
import { FinishSelect, FinishSelectItem } from "../../FinishSelect/FinishSelect";
import FinishWarning from "../../FinishWarning";
import { FinishOptionEdgeBand } from "../FinishOptionEdgeBand";

interface FinishOptionMultipleProps {
	finish: Finish;
	disabled: boolean;
}

const FinishOptionMultiple: FC<FinishOptionMultipleProps> = ({ finish, disabled }) => {
	// Because this component sets the data on all the sides, get the first
	// side and act like we are reading all the sides from this one.
	const intl = useIntl();
	const side = finish.availableOnSides.at(0);

	const { notify } = useNotifications();
	const { workpiece, setCanvasSettings } = usePostDataContext();
	const { appliedFinish } = useFinishSide({ side, intl });

	const {
		getSelectedFinishesOnSide,
		applyFinishOnSides,
		applyFinishOptionOnSides,
		applyFinishOptionParameterOnSides,
		removeFinishSides,
	} = useSelectedFinishes();

	const isChecked = useMemo(
		() =>
			finish.availableOnSides.every((side) =>
				getSelectedFinishesOnSide(side).includes(side.finishCode),
			),
		[finish.availableOnSides, getSelectedFinishesOnSide],
	);

	const isAlreadySelected = useMemo(
		() =>
			finish.availableOnSides.some((side) => {
				const finishes = getSelectedFinishesOnSide(side);

				return finishes.length > 0 && !finishes.includes(side.finishCode);
			}),
		[finish.availableOnSides, getSelectedFinishesOnSide],
	);

	const onCheckedChange = (state: Checkbox.CheckedState) => {
		if (state === true) {
			applyFinishOnSides(finish, finish.availableOnSides);
		} else {
			removeFinishSides(finish.availableOnSides);
		}
	};

	const [finishOptionValue, setFinishOptionValue] = useState<string>();

	const canSetValuesWithoutConsequences = useCallback(
		(option: FinishItemPostData["option"], parameters: FinishItemPostData["option_parameters"]) => {
			const fields = finish.options[option]?.constraints.fields;

			if (fields) {
				return Object.values(fields).some((field) => {
					return field.values?.some(({ value }) => Number(value) === Object.values(parameters)[0]);
				});
			}
		},
		[finish.options],
	);

	const setFinishOption = (
		option: FinishItemPostData["option"],
		parameters: FinishItemPostData["option_parameters"],
	) => {
		if (!canSetValuesWithoutConsequences(option, parameters)) {
			notify({
				variant: "warning",
				title: intl.formatMessage(
					{
						defaultMessage:
							"Afwerking is gereset, aangezien deze niet beschikbaar is voor {finish}",
						id: "i1K7xi",
					},
					{
						finish: finishOptionValue && finish.options[finishOptionValue]?.label,
					},
				),
			});
		}

		applyFinishOptionOnSides(finish, finish.availableOnSides, 0, option, parameters);
	};

	const sideFinish = useMemo(() => {
		if (side) {
			const currentSide = workpiece.finishes.side[side.sideCode];
			return currentSide ? currentSide[side.finishCode]?.[0] : undefined;
		}
	}, [side, workpiece.finishes.side]);

	const optionFields = useMemo(() => {
		if (isChecked && side) {
			return sideFinish?.option ? finish.options[sideFinish.option]?.constraints.fields : undefined;
		}
	}, [finish.options, isChecked, side, sideFinish?.option]);

	return (
		<FinishOptionCheckbox
			label={intl.formatMessage(
				{ defaultMessage: "{finish} op alle zijdes toepassen", id: "8jG0wi" },
				{ finish: finish.label },
			)}
			disabled={disabled || finish.mandatory}
			checked={isAlreadySelected ? "indeterminate" : isChecked}
			onCheckedChange={onCheckedChange}
			onMouseOver={() => setCanvasSettings({ view: "2d", field: "combined" })}
			onMouseLeave={() => setCanvasSettings(undefined)}
		>
			{isAlreadySelected && (
				<FinishWarning>
					<FormattedMessage
						defaultMessage="Er is al {finish} op één van de zijdes toegepast"
						id="2SVjkc"
						values={{
							finish:
								appliedFinish?.label?.toLocaleLowerCase() ??
								intl.formatMessage({ defaultMessage: "een afwerking", id: "gCcAUc" }),
						}}
					/>
				</FinishWarning>
			)}

			{isChecked && sideFinish && (
				<div className="flex">
					{finish.abstract_code === "absband" && finish.options.custom !== undefined ? (
						<FinishOptionEdgeBand finish={finish} disabled={disabled} />
					) : (
						<div className="flex w-full flex-wrap gap-2">
							<FinishSelect
								value={sideFinish.option}
								onValueChange={(option) => {
									setFinishOptionValue(option);

									return setFinishOption(option, sideFinish.option_parameters);
								}}
							>
								{Object.entries(finish.options).map(([code, finish]) => (
									<FinishSelectItem key={code} value={code}>
										<img
											src={finish.finish_image}
											alt={finish.label}
											width={300 / 6}
											height={120 / 6}
										/>
										<div>
											<div>{finish.label}</div>
											<div className="text-sm">
												{`${formatNumber({
													lang: intl.locale,
													value: getMoneyValue(finish.constraints.price),
													style: "decimal",
												})} p/m1`}
											</div>
										</div>
									</FinishSelectItem>
								))}
							</FinishSelect>

							{optionFields && (
								<Fragment>
									{Object.values(optionFields).map((field, index) => (
										<Fragment key={field.name}>
											{!!field.values && (
												<FinishSelect
													value={sideFinish.option_parameters[field.name]?.toString()}
													onValueChange={(value) =>
														applyFinishOptionParameterOnSides(
															finish.availableOnSides,
															index,
															field.name,
															value,
														)
													}
												>
													{field.values.map(({ value, label }) => (
														<FinishSelectItem key={value} value={value.toString()}>
															{label}
														</FinishSelectItem>
													))}
												</FinishSelect>
											)}
										</Fragment>
									))}
								</Fragment>
							)}
						</div>
					)}
				</div>
			)}

			{finish.mandatory && (
				<FinishWarning>
					<FormattedMessage defaultMessage="Deze afwerking is verplicht" id="C2FG5S" />
				</FinishWarning>
			)}
		</FinishOptionCheckbox>
	);
};

export default FinishOptionMultiple;
